/// Renders moving aircraft carriers — hull polygon, angled deck
/// runway slab, approach course, distance rings, and the
/// course-vector tick.
///
/// Built once at the airport's INITIAL projected position and
/// translated each tick by GameSession's airport-motion delta —
/// so all the visuals track the live deck without expensive
/// per-frame re-projection. The runway slab inside this layer
/// stays geometrically aligned with the runway's current
/// (mutated) start/end coordinates because we draw it relative
/// to those at construction time and translate the whole
/// container together.

import { Container, Graphics, Text, TextStyle } from 'pixi.js'
import type { CoordinateProjector } from '../geo/CoordinateProjector'
import type { Airport, Runway } from '../types/LevelData'
import { radarTheme } from './theme'

/// Hull polygon dimensions in NM — game-scale, NOT real-world.
/// A real Nimitz is ~0.18 NM × 0.04 NM; at a 60 NM radar zoom
/// that's 5 px × 1 px, basically invisible. Scaled up ~8× so the
/// hull reads as a ship at radar zoom and the runway slab fills
/// the deck visually. The runway endpoint coords in the level
/// JSON are scaled to match (1.5 NM apart).
const HULL_LENGTH_NM = 1.5
const HULL_BEAM_NM = 0.35
/// Approach course distance behind the angled deck threshold.
const APPROACH_LENGTH_NM = 8
/// Course vector — extends from the bow forward in the direction
/// of motion. Visual cue for "this airport is moving that way".
const COURSE_VECTOR_LENGTH_NM = 2

export class CarrierLayer extends Container {
  readonly airportName: string

  constructor(airport: Airport, projector: CoordinateProjector, pxPerNM: number) {
    super()
    this.airportName = airport.name
    if (!airport.motion) {
      // No motion = no carrier behavior; skip rendering.
      return
    }

    const heading = airport.motion.headingDeg
    const center = projector.project(airport.centerCoordinate, true)

    // Distance rings (drawn first so they sit underneath everything).
    if (airport.distanceRings && airport.distanceRings.length > 0) {
      for (const distNM of airport.distanceRings) {
        const radiusPx = distNM * pxPerNM
        const ring = new Graphics()
        ring.circle(center.x, center.y, radiusPx)
        ring.stroke({ width: 0.8, color: radarTheme.label, alpha: 0.25 })
        this.addChild(ring)
      }
    }

    // Hull — rectangle oriented along carrier course. iOS uses a
    // simple grey slab; we match (mid-grey, opaque) so the deck
    // reads as a ship hull, not a runway.
    this.addChild(buildHull(center, heading, pxPerNM))

    // Course vector — small arrow extending forward in the
    // direction of motion. Text label shows the carrier name.
    this.addChild(buildCourseVector(center, heading, pxPerNM))

    // Runway slabs (welded to the deck — drawn at runway's INITIAL
    // pixel coordinates). The container translation moves them
    // together with the hull each tick.
    for (const runway of airport.runways) {
      this.addChild(buildCarrierRunway(runway, projector))
    }

    // Approach course — extends behind the angled-deck threshold,
    // following the runway's heading1 (the landing direction).
    for (const runway of airport.runways) {
      this.addChild(buildApproach(runway, projector, pxPerNM))
    }

    // Carrier name label, set just outside the hull.
    this.addChild(buildLabel(airport.name, center, heading, pxPerNM))
  }

  /// Translate the entire layer by a pixel delta — called each
  /// tick from RadarScene with `session.airportTranslationPx(...)`.
  setTranslation(delta: { x: number; y: number }): void {
    this.position.set(delta.x, delta.y)
  }
}

function buildHull(
  center: { x: number; y: number },
  headingDeg: number,
  pxPerNM: number,
): Graphics {
  // Hull is a rectangle, half-length forward of center, half-aft.
  // Heading 0° = North (screen-up = -y); convert to screen angle
  // for the hull's long axis: forward = (sin h, -cos h).
  const headingRad = (headingDeg * Math.PI) / 180
  const fwdX = Math.sin(headingRad)
  const fwdY = -Math.cos(headingRad)
  const sideX = -fwdY // perpendicular: rotate forward 90° clockwise
  const sideY = fwdX

  const halfLenPx = (HULL_LENGTH_NM / 2) * pxPerNM
  const halfBeamPx = (HULL_BEAM_NM / 2) * pxPerNM

  const corner = (forward: number, side: number) => ({
    x: center.x + fwdX * halfLenPx * forward + sideX * halfBeamPx * side,
    y: center.y + fwdY * halfLenPx * forward + sideY * halfBeamPx * side,
  })
  // Bow is a rounded point; draw as four corners with the bow tapered.
  // For MVP use a plain rectangle — readable at radar zoom.
  const c1 = corner(1, -1)
  const c2 = corner(1, 1)
  const c3 = corner(-1, 1)
  const c4 = corner(-1, -1)

  const g = new Graphics()
  g.moveTo(c1.x, c1.y)
  g.lineTo(c2.x, c2.y)
  g.lineTo(c3.x, c3.y)
  g.lineTo(c4.x, c4.y)
  g.lineTo(c1.x, c1.y)
  g.fill({ color: 0x4a5568, alpha: 0.9 })
  g.stroke({ width: 1, color: radarTheme.runway, alpha: 0.7 })
  return g
}

function buildCourseVector(
  center: { x: number; y: number },
  headingDeg: number,
  pxPerNM: number,
): Graphics {
  const headingRad = (headingDeg * Math.PI) / 180
  const fwdX = Math.sin(headingRad)
  const fwdY = -Math.cos(headingRad)
  const lenPx = COURSE_VECTOR_LENGTH_NM * pxPerNM
  const startPx = (HULL_LENGTH_NM / 2) * pxPerNM
  const g = new Graphics()
  g.moveTo(center.x + fwdX * startPx, center.y + fwdY * startPx)
  g.lineTo(
    center.x + fwdX * (startPx + lenPx),
    center.y + fwdY * (startPx + lenPx),
  )
  g.stroke({ width: 1, color: radarTheme.track, alpha: 0.6 })
  return g
}

function buildCarrierRunway(runway: Runway, projector: CoordinateProjector): Graphics {
  // Carrier "runway" = angled deck strip. Author the JSON with the
  // start/end coords offset 10° from the carrier's hull centerline;
  // we draw it as a thin slab here.
  const start = projector.project(runway.startCoordinate, true)
  const end = projector.project(runway.endCoordinate, true)
  const dx = end.x - start.x
  const dy = end.y - start.y
  const length = Math.hypot(dx, dy)
  if (length < 1) return new Graphics()
  const ux = dx / length
  const uy = dy / length
  // Perpendicular (right-hand) unit vector.
  const px = uy
  const py = -ux
  const halfWidth = 4 // px — narrower than land runway slabs
  const corners = [
    { x: start.x + px * halfWidth, y: start.y + py * halfWidth },
    { x: end.x + px * halfWidth, y: end.y + py * halfWidth },
    { x: end.x - px * halfWidth, y: end.y - py * halfWidth },
    { x: start.x - px * halfWidth, y: start.y - py * halfWidth },
  ]
  const g = new Graphics()
  g.moveTo(corners[0]!.x, corners[0]!.y)
  g.lineTo(corners[1]!.x, corners[1]!.y)
  g.lineTo(corners[2]!.x, corners[2]!.y)
  g.lineTo(corners[3]!.x, corners[3]!.y)
  g.lineTo(corners[0]!.x, corners[0]!.y)
  g.fill({ color: radarTheme.runway, alpha: 0.95 })
  return g
}

function buildApproach(
  runway: Runway,
  projector: CoordinateProjector,
  pxPerNM: number,
): Graphics {
  // Approach extends backward from the LANDING end (runway.startCoordinate
  // by convention — author the JSON so heading1 is the landing direction).
  const start = projector.project(runway.startCoordinate, true)
  const end = projector.project(runway.endCoordinate, true)
  const dx = end.x - start.x
  const dy = end.y - start.y
  const length = Math.hypot(dx, dy)
  if (length < 1) return new Graphics()
  // Backward unit vector (away from the deck, behind the threshold).
  const ux = -dx / length
  const uy = -dy / length
  const tipX = start.x + ux * APPROACH_LENGTH_NM * pxPerNM
  const tipY = start.y + uy * APPROACH_LENGTH_NM * pxPerNM
  const g = new Graphics()
  g.moveTo(start.x, start.y)
  g.lineTo(tipX, tipY)
  g.stroke({ width: 1, color: radarTheme.centerline, alpha: 0.4 })
  return g
}

function buildLabel(
  name: string,
  center: { x: number; y: number },
  headingDeg: number,
  pxPerNM: number,
): Text {
  // Label sits abeam (perpendicular to course) so it doesn't sit
  // on the angled deck.
  const headingRad = (headingDeg * Math.PI) / 180
  const fwdX = Math.sin(headingRad)
  const fwdY = -Math.cos(headingRad)
  const sideX = -fwdY
  const sideY = fwdX
  const offsetPx = (HULL_BEAM_NM * 1.5) * pxPerNM
  const style = new TextStyle({
    fontFamily: 'Helvetica, Arial, sans-serif',
    fontSize: 9,
    fontWeight: '600',
    fill: radarTheme.label,
  })
  const t = new Text({ text: name, style })
  t.anchor.set(0, 0.5)
  t.position.set(center.x + sideX * offsetPx, center.y + sideY * offsetPx)
  t.resolution = 2
  return t
}
